import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { Dial } from "./Dial";

type SavingsDataT = {
  utilityBill: number;
  solarPayment: number;
  monthlySavings: number;
  annualSavings: number;
  twentyFiveYearSavings: number;
};

type AnimatedValuesT = {
  utilityBill: number;
  solarPayment: number;
  monthlySavings: number;
  annualSavings: number;
  twentyFiveYearSavings: number;
};

const DEFAULT_BILL = 200;

const calcSavings = (utilityBill: number): SavingsDataT => {
  const solarPayment = Math.max(79, Math.round(utilityBill * 0.62));
  const monthlySavings = utilityBill - solarPayment;
  const annualSavings = monthlySavings * 12;
  let twentyFiveYearSavings = 0;
  for (let i = 0; i < 25; i++) {
    twentyFiveYearSavings += (Math.round(utilityBill * 1.04 ** i) - solarPayment) * 12;
  }
  return { utilityBill, solarPayment, monthlySavings, annualSavings, twentyFiveYearSavings };
};

const formatDollars = (val: number): string => `$${Math.round(val).toLocaleString("en-US")}`;

const SavingsWidget = () => {
  const { t } = useTranslation("static_savings_widget");
  const [utilityBill, setUtilityBill] = useState(DEFAULT_BILL);
  const target = calcSavings(utilityBill);

  const animatedRef = useRef<AnimatedValuesT>({
    utilityBill: target.utilityBill,
    solarPayment: target.solarPayment,
    monthlySavings: target.monthlySavings,
    annualSavings: target.annualSavings,
    twentyFiveYearSavings: target.twentyFiveYearSavings,
  });

  const [displayed, setDisplayed] = useState<AnimatedValuesT>({
    utilityBill: target.utilityBill,
    solarPayment: target.solarPayment,
    monthlySavings: target.monthlySavings,
    annualSavings: target.annualSavings,
    twentyFiveYearSavings: target.twentyFiveYearSavings,
  });

  const rafRef = useRef<number | null>(null);
  const targetRef = useRef<SavingsDataT>(target);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  useEffect(() => {
    const animate = () => {
      const tgt = targetRef.current;
      const a = animatedRef.current;
      const lerp = (from: number, to: number) => from + (to - from) * 0.15;

      animatedRef.current = {
        utilityBill: lerp(a.utilityBill, tgt.utilityBill),
        solarPayment: lerp(a.solarPayment, tgt.solarPayment),
        monthlySavings: lerp(a.monthlySavings, tgt.monthlySavings),
        annualSavings: lerp(a.annualSavings, tgt.annualSavings),
        twentyFiveYearSavings: lerp(a.twentyFiveYearSavings, tgt.twentyFiveYearSavings),
      };

      setDisplayed({ ...animatedRef.current });

      const done = (Object.keys(tgt) as (keyof SavingsDataT)[]).every(
        (k) => Math.abs(animatedRef.current[k] - tgt[k]) < 0.5,
      );

      if (!done) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  const dialCenter = (
    <>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          fontSize: { xs: "2rem", md: "2.5rem" },
          color: "white",
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}
      >
        {formatDollars(displayed.utilityBill)}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: "0.68rem",
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.35)",
          mt: 0.5,
        }}
      >
        {t("dialCenter.billLabel")}
      </Typography>
    </>
  );

  const dialSubText = (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        sx={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: "0.65rem",
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.25)",
        }}
      >
        {t("dialSubText.dragToAdjust")}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Typography
        align="center"
        sx={{
          fontWeight: 600,
          color: "rgba(255,255,255,0.35)",
          fontSize: "0.72rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontFamily: "'Barlow', sans-serif",
          mb: { xs: 4, md: 6 },
        }}
      >
        {t("instruction")}
      </Typography>
      <Box sx={{ display: { xs: "block", md: "flex" }, gap: { md: "48px" }, alignItems: "center" }}>
        <Box sx={{ flex: "0 0 auto", width: { xs: "100%", md: "42%" }, mb: { xs: 4, md: 0 } }}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: { xs: 320, md: 400 },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                "@keyframes floatCaret": {
                  "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
                  "50%": { transform: "translateX(-50%) translateY(-8px)" },
                },
                animation: "floatCaret 2.4s ease-in-out infinite",
                zIndex: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.5)",
                  fontFamily: "'Barlow', sans-serif",
                }}
              >
                {t("caret.dragLabel")}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: "1.4rem", color: "rgba(255,255,255,0.5)" }} />
            </Box>
            <Dial
              value={utilityBill}
              min={0}
              max={1000}
              increment={10}
              size={360}
              onChange={setUtilityBill}
              subText={dialSubText}
            >
              {dialCenter}
            </Dial>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "20px",
              p: { xs: 4, md: 5 },
              boxShadow: "0 0 48px rgba(32,194,241,0.06), inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ borderLeft: "3px solid #F4C751", pl: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "rgba(244,199,81,0.7)",
                      mb: 1,
                    }}
                  >
                    {t("labels.currentUtility")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 800,
                      fontSize: { xs: "2rem", md: "2.6rem" },
                      lineHeight: 1,
                      background: "linear-gradient(90deg, #F4C751 0%, #fbd96a 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      mb: 0.75,
                    }}
                  >
                    {formatDollars(displayed.utilityBill)}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        WebkitTextFillColor: "rgba(244,199,81,0.6)",
                        color: "rgba(244,199,81,0.6)",
                        ml: 0.5,
                      }}
                    >
                      {t("units.perMonth")}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "1rem",
                      color: "rgba(255,255,255,0.5)",
                      fontWeight: 500,
                    }}
                  >
                    {t("footnotes.utilityRising")}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ borderLeft: "3px solid #51F2CB", pl: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "rgba(81,242,203,0.7)",
                      mb: 1,
                    }}
                  >
                    {t("labels.withSolar")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 800,
                      fontSize: { xs: "2rem", md: "2.6rem" },
                      lineHeight: 1,
                      background: "linear-gradient(90deg, #51F2CB 0%, #20C2F1 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      mb: 0.75,
                    }}
                  >
                    {formatDollars(displayed.solarPayment)}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        background: "none",
                        WebkitTextFillColor: "rgba(81,242,203,0.6)",
                        color: "rgba(81,242,203,0.6)",
                        ml: 0.5,
                      }}
                    >
                      {t("units.perMonth")}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "1rem",
                      color: "rgba(255,255,255,0.5)",
                      fontWeight: 500,
                    }}
                  >
                    {t("footnotes.solarLocked")}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 4,
                pt: 4,
                borderTop: "1px solid rgba(32,194,241,0.15)",
              }}
            >
              <Box sx={{ borderLeft: "3px solid #20C2F1", pl: 2, mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "rgba(32,194,241,0.7)",
                  }}
                >
                  {t("labels.youSave")}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: "2.5rem", md: "3.2rem" },
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  background: "linear-gradient(90deg, #20C2F1 0%, #67e8f9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  mb: 0.5,
                }}
              >
                {formatDollars(displayed.monthlySavings)}
                <Box
                  component="span"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 500,
                    WebkitTextFillColor: "rgba(32,194,241,0.5)",
                    color: "rgba(32,194,241,0.5)",
                    ml: 1,
                  }}
                >
                  {t("units.month")}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: { xs: "1rem", md: "1.15rem" },
                  color: "rgba(255,255,255,0.5)",
                  fontWeight: 400,
                  mb: 1,
                }}
              >
                {t("units.perYear", { amount: formatDollars(displayed.annualSavings) })}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: "1.4rem", md: "1.8rem" },
                  lineHeight: 1.2,
                  color: "white",
                  mb: 0.25,
                }}
              >
                ~{t("units.over25Years", { amount: formatDollars(displayed.twentyFiveYearSavings) })}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "1rem",
                  color: "rgba(255,255,255,0.55)",
                  fontStyle: "italic",
                }}
              >
                {t("footnotes.utilityInflation")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export { SavingsWidget };
